import React, { useMemo } from "react";
import { observer } from "mobx-react-lite";

import { useRootStore } from "../../../../store/contexts";
import { useDataGridHandlers } from "../../../../common/hooks";
import {
  DataGrid,
  DataGridColumn,
  DataGridRowAction,
} from "../../../../common/controls/lists/DataGrid";
import { CreditPurchase } from "../../../../api/graphql";
import { MyCreditPurchasesListStore } from "../../store";
import { tenantDisplayId } from "../../../tenant/store/TenantCommonStore";
import { diaDeCalendario, formatearDia } from "../../../../common/calendarDate";

import { VisibilityIcon } from "../../../../icons";

export type GridExpressProps = { listStore?: MyCreditPurchasesListStore };

export const MyCreditPurchasesListForm: React.FC<GridExpressProps> = observer(
  ({ listStore }) => {
    const rootStore = useRootStore();
    const { langStore } = rootStore;

    if (!listStore) {
      return null;
    }

    const store = listStore;

    // Handlers de DataGrid (extraídos a hook común)
    const {
      handleColumnVisibilityChange,
      handleColumnOrderChange,
      handleColumnSizingChange,
      handleFiltersChange,
      handleSortingChange,
      handlePaginationChange,
    } = useDataGridHandlers(store, rootStore);

    // Row actions
    const rowActions: DataGridRowAction<CreditPurchase>[] = useMemo(
      () => [
        {
          label: langStore.viewCommand,
          onClick: (row: CreditPurchase) =>
            rootStore.router.goTo("/my-credit-purchases/view/" + row.id),
          icon: <VisibilityIcon fontSize="small" />,
        },
      ],
      [langStore, store, rootStore.router]
    );

    // Columnas del store con accessorFn para referencias
    const columns = useMemo(() => {
      const storeColumns = store.grid
        .dataGridColumns as unknown as DataGridColumn<CreditPurchase>[];
      return storeColumns.map((col) => {
        if (col.accessorKey === "tenant") {
          return {
            ...col,
            id: "tenant",
            accessorFn: (row: CreditPurchase) =>
              row.tenant ? tenantDisplayId(row.tenant, langStore) : "-",
          };
        }
        if (col.accessorKey === "purchaseDate") {
          return {
            ...col,
            id: "purchaseDate",
            accessorFn: (row: CreditPurchase) =>
              formatearDia(
                diaDeCalendario(row.purchaseDate),
                langStore.timeFormat
              ) || "-",
          };
        }
        if (col.accessorKey === "expirationDate") {
          return {
            ...col,
            id: "expirationDate",
            accessorFn: (row: CreditPurchase) =>
              formatearDia(
                diaDeCalendario(row.expirationDate),
                langStore.timeFormat
              ) || "-",
          };
        }
        return col;
      });
    }, [store.grid.dataGridColumns, langStore]);

    const rows = store.creditPurchaseCurrentPage || [];

    return (
      <div id={"ListOfCreditPurchase"}>
        <div id={"StartOfListingOfCreditPurchase"} />
        <DataGrid
          columns={columns}
          data={rows}
          isLoading={store.status === "fetching"}
          enableFiltering
          enableShowHideFilters
          onFiltersChange={handleFiltersChange}
          initialFilters={store.grid.filtering}
          sorting={store.grid.sorting.map((s) => ({
            id: s.columnName,
            desc: s.direction === "desc",
          }))}
          onSortingChange={handleSortingChange}
          pagination={{
            pageIndex: store.currentPage - 1,
            pageSize: store.pageSize,
          }}
          onPaginationChange={handlePaginationChange}
          rowCount={store.totalRows + (store.hasMorePages ? 1 : 0)}
          manualPagination
          enableColumnOrdering
          enableColumnResizing
          enableHiding
          columnVisibility={store.grid.mrtColumnVisibility}
          onColumnVisibilityChange={handleColumnVisibilityChange}
          columnOrder={store.grid.columnOrder}
          onColumnOrderChange={handleColumnOrderChange}
          columnSizing={store.grid.mrtColumnSizing}
          onColumnSizingChange={handleColumnSizingChange}
          showColumnFilters={store.grid.showColumnFilters}
          onShowColumnFiltersChange={(value) =>
            store.grid.setShowColumnFilters(value)
          }
          isFullScreen={store.grid.isFullScreen}
          onIsFullScreenChange={(value) => store.grid.setIsFullScreen(value)}
          getRowId={(row) => row.id}
          language={langStore.language as "es" | "en" | "pt"}
          onRowClick={(row, event) => store.handleRowClick(row, event)}
          selectedRowId={store.selectedRowId}
          rowActions={rowActions}
          muiTableBodyRowProps={() => ({
            sx: { cursor: "pointer" },
          })}
        />
        <div id={"EndOfListing"} />
      </div>
    );
  }
);
